import { authenticatedInstance } from '../index/index.service';
import type { ActiveSessionView, SessionSalesSummaryView, SessionView } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { ActiveSessionView, SessionSalesSummaryView, SessionState, SessionView } from 'shared-models/browser';

// Raised when close is rejected because orders opened mid-flow; count is null when unparsed.
export class SessionHasOpenOrdersError extends Error {
  constructor(public readonly openOrderCount: number | null) {
    super('SESSION_HAS_OPEN_ORDERS');
    this.name = 'SessionHasOpenOrdersError';
  }
}

// Result of a manager close attempt, driving the caller's print-vs-guard decision.
export type SessionCloseOutcome =
  | { status: 'closed' }
  | { status: 'open-orders'; openOrderCount: number | null }
  | { status: 'error' };

class SessionsService {
  // Summary: Loads the active session + auto-close time so the shell gates and renders the tile.
  // Details: docs/function-library/web/services.md#sessions-service
  async getActive(tenantId: string, employeeSession: string): Promise<ActiveSessionView> {
    try {
      const response = await authenticatedInstance.get<ActiveSessionView>(`/sessions/tenant/${tenantId}/active`, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Loads closed-order count + sales total for the active session, powering the close-session ("Z סוף יום") header.
  async getSummary(tenantId: string, employeeSession: string): Promise<SessionSalesSummaryView> {
    try {
      const response = await authenticatedInstance.get<SessionSalesSummaryView>(
        `/sessions/tenant/${tenantId}/summary`,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Opens a tenant session from POS flow; declares opening cash float when provided.
  // Details: docs/function-library/web/services.md#sessions-service
  async open(tenantId: string, employeeSession: string, openingCash?: number): Promise<SessionView> {
    try {
      const body = openingCash != null ? { openingCash } : {};
      const response = await authenticatedInstance.post<SessionView>(
        `/sessions/tenant/${tenantId}/open`,
        body,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Manager corrects the open session's opening cash. Void — SSE echoes the update.
  // Details: docs/function-library/web/services.md#sessions-service
  async updateOpeningCash(tenantId: string, employeeSession: string, openingCash: number): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/opening-cash`,
        { openingCash },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating opening cash'));
    }
  }

  // Summary: Manager sets the open session's daily brief. Returns void — update arrives via SSE.
  // Details: docs/function-library/web/services.md#sessions-service
  async updateDailyBrief(tenantId: string, text: string, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/daily-brief`,
        { text },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating daily brief'));
    }
  }

  // Summary: Manager set/clear (null = off) the tenant session auto-close time. Void — rides SSE.
  // Details: docs/function-library/web/services.md#sessions-service
  async updateAutoCloseTime(tenantId: string, autoCloseTime: string | null, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/auto-close-time`,
        { autoCloseTime },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating auto-close time'));
    }
  }

  // Summary: Manager sets the tenant-wide POS floor-plan canvas background. Void — rides SSE to every device.
  async updateFloorBackground(
    tenantId: string,
    floorBackground: 'white' | 'image' | 'primary',
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/floor-background`,
        { floorBackground },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating floor background'));
    }
  }

  // Summary: Manager toggles whether employee general-item actions require manager approval.
  async updateGeneralItemApprovalRequired(
    tenantId: string,
    generalItemRequiresManagerApproval: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/general-item-approval`,
        { generalItemRequiresManagerApproval },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating general item approval setting'));
    }
  }

  // Summary: Super-admin manager toggles refund 2FA code delivery (email default vs PWA push).
  async updateRefundCodeViaPush(
    tenantId: string,
    refundCodeViaPush: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/refund-code-via-push`,
        { refundCodeViaPush },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating refund code channel setting'));
    }
  }

  // Summary: Auto-closed sessions (last 24h) whose Z is unprinted — reconnect catch-up list.
  async listPendingAutoCloseZPrints(tenantId: string, employeeSession: string): Promise<number[]> {
    try {
      const response = await authenticatedInstance.get<number[]>(
        `/sessions/tenant/${tenantId}/pending-z-prints`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading pending Z-prints'));
    }
  }

  // Summary: Claims the single Z-print for an auto-closed session; true only for the winner.
  async claimAutoCloseZPrint(tenantId: string, sessionId: number, employeeSession: string): Promise<boolean> {
    try {
      const response = await authenticatedInstance.post<{ won: boolean }>(
        `/sessions/tenant/${tenantId}/${sessionId}/claim-z-print`,
        {},
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data.won;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed claiming Z-print'));
    }
  }

  // Summary: Closes the currently active tenant session from manager settings controls.
  // Details: docs/function-library/web/services.md#sessions-service
  async close(tenantId: string, employeeSession: string, cashDrawerPrinterId?: number): Promise<SessionView> {
    try {
      const body = cashDrawerPrinterId != null ? { cashDrawerPrinterId } : {};
      const response = await authenticatedInstance.patch<SessionView>(
        `/sessions/tenant/${tenantId}/close`,
        body,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } }).response?.status;
      const message = extractServiceErrorMessage(error, '');
      if (status === 409 && /order\(s\) still open/i.test(message)) {
        const match = message.match(/(\d+)\s+order/i);
        throw new SessionHasOpenOrdersError(match ? Number(match[1]) : null);
      }
      throw new Error(message);
    }
  }
}

export const sessionsService = new SessionsService();





